import type { AlertFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job, type Collaborator } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { required, validationContext } from 'validx'

/**
 * View Model for the Remove Clients as non-Admin dialog.
 */
export class RemoveClientsAsNonAdminDialogViewModel {
  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * Collaborator to remove.
   */
  @observable accessor collaborator: Collaborator | undefined = undefined

  /**
   * Disables submit button.
   */
  @observable accessor disabled = true

  /**
   * Validation context for View Model.
   */
  readonly validation = validationContext(this, {
    collaborator: [required('Collaborator is required.')],
  })

  constructor(
    private readonly _job: Job,
    private readonly _jobStore: JobStore,
    private readonly _newAlert: AlertFactory,
  ) {}

  /**
   * Supporting Clients on the Job.
   */
  @computed
  get supportingClients() {
    return this._job.supportingClients
  }

  /**
   * Filtered collaborators.
   */
  @computed
  get filteredCollaborators() {
    return this.supportingClients.filter((supportingCollaborator) =>
      this._job.collaborators.some(
        (c) =>
          c.member === supportingCollaborator.member && c.role === 'Supporting',
      ),
    )
  }

  /**
   * Sets a collaborator to remove.
   */
  @action.bound
  setCollaborator(collaborator?: Collaborator) {
    this.collaborator = collaborator
    this.disabled = !this.validate()
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.collaborator = undefined
    this.disabled = true

    this.removeClient.reset()
    this.validation.reset()
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Removes a client from the job.
   */
  removeClient = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const collaborator = this._job.collaborators.find(
      (c) => c.member === this.collaborator?.member,
    )

    if (collaborator) {
      const alert = this._newAlert()
        .withState('pending')
        .saying(
          'Removing Client',
          `Removing ${collaborator.member.displayName} from ${this._job.name}...`,
        )
        .show()

      try {
        await this._jobStore.removeClientAsNonAdmin(
          this._job.id,
          collaborator.member.user.publicId,
        )
      } catch (err) {
        alert.dismiss()
        throw err
      }

      alert
        .withState('success')
        .saying(
          'Success!',
          `${collaborator.member.displayName} was removed from ${this._job.name}.`,
        )
        .autoDismiss()
    }

    this.modal.close()
  })

  /**
   * Shows RemoveClientsAsNonAdminDialog.
   */
  @action.bound
  show() {
    if (this.modal.showing) {
      return
    }

    this.modal.show()
  }
}
